// Range Finder state machine (issues #3481, #3510): a pure reducer over the steps of one run,
// driven by microphone frames and the page's actions. No React, no DOM, no timers, no audio. The
// page holds the state in useReducer, subscribes to the Learn audio session's frames and dispatches
// each one, plays the piano guide once per guide request and dispatches `guideStarted` when it is
// scheduled, and renders from the state and the selectors below. Stories and tests drive it the
// same way with synthetic frames.
//
// Every time in this module is absolute ms on the audio clock: frames carry it (CaptureFrame.timeMs)
// and `guideStarted` carries the time the guide was scheduled for on the same clock. Nothing here
// reads a wall clock, so the elapsed times that decide a match, a hint and a nudge come only from
// the frames the mic delivers.
//
// Phases:
//
//   permission ──micOpened──▶ listen (lower) ──note held──▶ step (lower) ──▶ done (lower)
//       │                                                                        │
//       │ micFailed                                            continueAfterDone │
//       ▼                                                                        ▼
//     failed        result ◀── done (higher) ◀── step (higher) ◀──note held── listen (higher)
//                       continueAfterDone
//
// The microphone is the only way this run can fail (issue #3510): every other phase either waits
// or moves on, so the singer is never told the exercise is over because of what they sang.
//
// A listen phase asks the singer for a note of their own ('Please sing "La" as low as you can', and
// the high equivalent for the ascent) and waits, as long as it takes, for a confident pitch held
// steady for listenHoldMs. That note counts: it goes straight into the matched notes, so a note the
// singer plainly sang is in their range whatever happens next. The hold forgives a dip shorter than
// matchGapMs, exactly as a step's does (issue #3559): a frame or two the detector makes nothing of
// in the middle of a held note - YIN's confidence wavering, a breath, a moment of room noise - is
// the same note still, and only a longer dip is one note that stopped and another that started.
// Forgiving a dip is not forgiving a hold with nothing in it, though: the hold has to have been
// carried by confident frames for listenMinConfidentFraction of the hold time before it identifies
// anything, so a room hum whose clarity flickers across the bar once every grace period - alive
// frame after frame, confident in hardly any of them - is never recorded as the note the singer
// picked. While no confident pitch arrives the phase counts what it hears instead, silence and noise apart:
// after noToneAfterMs of quiet `isTroubleHearing` is true (we cannot hear them) and after as long of
// sound we can make no note of `isNoisy` is true (we cannot hear them through the room), so the
// screen can say which. Each count is the run going on now: a confident frame clears both, and
// either kind clears the other, so the screen follows the room rather than a count it stopped
// earning. Nothing here ends the run.
//
// The opening prompt stays up for listenPromptMs whatever the singer does, in both directions. A
// singer who starts singing before they have read what they are asked to do would otherwise be told
// "Hold it" at once and moved on to the stepping phase a second later, before they knew what the
// exercise was. So for that long the listen phase scores nothing, exactly as it scores nothing
// during the settle below: frames move the meter (they can see they are heard) and the clock, and
// nothing else - no hold is started or credited, no tone is heard (so the scene says the prompt and
// never "Hold it"), and the silence and noise counts do not run, so neither of their bubbles can
// take the prompt's place either: both counts start where the window ends, and the noToneAfterMs
// that earns either bubble is all time the singer has had the prompt to read. The
// window is counted from the first frame the phase is fed, which is the frame clock's first reading
// of the prompt on screen: for the descent the first frame after the microphone opened, for the
// ascent the first after the page dispatched `continueAfterDone`, which it does once the done
// sequence and the "Now let's go higher!" announcement have finished. The phase records that frame
// as `promptFromMs`, because the frame clock has no other reading of when it began: the run's first
// listen is created before any frame has arrived, and the frames the ascent inherits were the done
// phase's. A singer still holding a note when the window ends is heard from that frame on, and is
// credited listenHoldMs of steady pitch after it, never with what they sang inside it.
//
// A done phase ends each direction (issue #3510): the stop button, the give-up and the bound all
// land there rather than going straight on, and the page dispatches `continueAfterDone` when the
// scene has finished saying so - the singer hopping onto the landing, and for the descent the
// handover to the ascent. Nothing is scored in done: frames only move the meter and the frame clock,
// so the ascent can never take the descent's last note (or the announcement it is played under) for
// the note the singer picked. The descent's done continues into the ascent's listen phase, the
// ascent's into the result.
//
// Each direction's own notes are `matched` from `directionStart` on (see `directionNotes`): 0 for
// the descent, and whatever the descent left behind for the ascent, fixed when the ascent's listen
// phase is created. The scene draws one tread per note of the direction, so it reads them from
// there. The octave rule rewrites the direction's sung note in place rather than appending, so the
// slice stays the direction's.
//
// A listen phase that follows a direction settles first, and the settle is counted from the moment
// the direction ended (issue #3559): the done phase records that frame as `endedAtMs`, and the
// listen phase that follows scores nothing before guideTailMs past it (`scoreFromMs`). What there is
// to settle for is only the echo canceller: the guide is cancelled at the step-to-done transition,
// so the tail a guide that really sounded gets is the whole of it, and a guide's own length on top
// of it is time nothing was sounding through. Anchoring the settle to the end rather than to the
// prompt covers a continue dispatched early (a skip tap, a story): the tail is honoured either way.
// The very first listen has no direction behind it, so it has no settle. A listen phase scores from
// the later of its settle and the end of its prompt window, and with the tunables as they stand the
// window always ends later - it is seconds long and starts after the direction ended, the tail a
// quarter of a second from that end - so the settle is what protects the ascent only if a device
// pass ever shortens the window below the tail.
//
// A step is one target note: one C major scale step past the note just sung, then one step further
// each time. The page plays the guide (the piano soundfont, never a bare tone), then the machine
// listens: frames before `listenFromMs` are ignored, so the guide never sounds while a match is
// scored. Each scored frame is one of three things (issue #3510):
//
//   - reached: the singer on the target, or past it on the direction's side (going lower, a pitch
//     more than matchCents below it, or the estimator's octave below). Those are the same thing to
//     the singer - they are at least as far as we asked - so they are one hold, keeping the running
//     mean of what they sang; a voice wobbling across the line goes on holding instead of starting
//     over on every frame. Held for matchHoldMs, they are credited with the nearest scale note on
//     the target's side of that mean, never past the target itself, and the guide steps one note
//     beyond that: an on-target hold records the target, and an overshoot lets the guide catch up
//     with them rather than ask again for something they have passed. Frames that stop arriving for
//     longer than matchGapMs start the hold over: two frames either side of a stall were never one
//     held note.
//   - trying (going lower, voiced sound above the target, or anything the estimator makes nothing
//     of): tryingMs accumulates. At hintAfterMs `isHinted` is true so the screen can say which way
//     to go, and at giveUpAfterMs this is as far as they go and the direction ends.
//   - silence: it never counts toward tryingMs, and after silenceReplayMs the note replays with the
//     nudge.
//
// One line shows under the stepping prompt: the nudge while `nudged` is set, and the hint otherwise.
// The first scored frame carrying a confident pitch clears the nudge, so as soon as the singer
// sings again the hint - the line that says what to do differently - has the space back, while a
// breath or a noisy room leaves the nudge where it is. A new target clears both (tryingMs with
// them); a replay keeps what they have spent trying, up to the hint, because hearing the note again
// is not a new target but it is a fresh go at it.
//
// What the scene says is smoothed where the scoring is not (issue #3510, addendum 4): `lastToneMs`
// records the last frame the singer was heard on a note - any confident pitch a listen phase
// scores, a frame at or past the target while stepping - and `isHearingTone` reads it as a tone for
// toneGraceMs afterwards, so a dip in the detector's confidence does not flicker the speech bubble
// between "Hold it" and the prompt. No hold and no credit depends on it.
//
// The bound and the manual stop end a direction too, into the same done phase: the descent's
// continues into the ascent's listen phase, and the ascent's into the result, whose range is the
// extremes of every matched note - both sung notes and every matched target.
//
// Octave rule. A general detector can read a held note an octave off, so the note a listen phase
// identifies can land an octave from where the singer is. On the direction's first target that is
// what an octave reading means whichever side it falls on: the first target is one scale step from
// the note they sang, so a singer who answers it an octave away almost certainly sang the listen
// note in that octave too. While `firstNote` holds, then, holding an octave off the target shifts
// the sung note into the singer's octave and records the target's octave with it, and the run goes
// on from there (so the range never keeps the octave the singer did not sing in). Once a note has
// been recorded the rule is over: an octave on the direction's side is simply reached, and one on
// the wrong side is sound that is not far enough.

import { learnListeningTunables, voicePartForRange } from '../../../../../common/src/learn_rules.ts';
import { VocalRange, VoicePart } from '../../../../../common/src/model.ts';
import { isNatural } from '../../../../../common/src/pitch_match.ts';
import { centsBetween, frequencyToMidi, midiToFrequency } from '../../../audio/pitch/notes.ts';
import { frameDelta, isSilent, levelForDbfs } from '../audio/frame_reading.ts';
import { CaptureFrame, MicFailure } from '../audio/learn_mic_capture.ts';

// ---- Tunables --------------------------------------------------------------------------------
// Grouped so the device validation pass (issue #3338, slice 8) can adjust them in one place.

export const rangeFinderTunables = {
  // The listening thresholds and the guide's own timings, shared with Pitch Match: matchGapMs,
  // voicedDbfs, voicedClarity, octaveMargin, frameDeltaCapMs, guideMs, guideTailMs and
  // silenceReplayMs (silence in one step before the note replays with the nudge). The set carries
  // a few numbers this exercise never asks for - unvoicedDbfs, dotClarity, tooNoisyAfterMs - which
  // are Pitch Match's: they are spread in whole rather than picked over, so one place still holds
  // what a device pass tunes.
  ...learnListeningTunables,
  matchCents: 100, // within this of the target counts as on the note
  matchHoldMs: 500, // a match must persist this long
  octaveGuardCents: 100, // this near an exact octave past the target, a raw YIN reading needs corroborating
  hintAfterMs: 2000, // voiced-but-not-far-enough time in one step before the screen hints which way
  giveUpAfterMs: 8000, // and before the direction ends: this is as far as the singer goes
  listenHoldMs: 1000, // a steady pitch this long is the note the singer picked
  // How long a listen phase's opening prompt ('Sing "La" as low as you can') stays up before
  // anything the singer does can replace it or move the run on: a singer who starts before they
  // have read it would otherwise get "Hold it" at once and the stepping phase a second later,
  // without knowing what they had been asked to do (both directions)
  listenPromptMs: 5000,
  listenSteadyCents: 100,
  // How much of that hold has to have been carried by confident frames before it is the singer's
  // note: a voice reads confidently in nearly every frame, and a hum that flickers across the
  // clarity bar does not, however long the grace keeps its hold alive (issue #3559)
  listenMinConfidentFraction: 0.6,
  noToneAfterMs: 8000, // listen: silence (or noise) this long and the screen says which we are hearing
  toneGraceMs: 700, // the scene goes on hearing a tone this long past the last confident frame
  minMidi: 40, // E2: the descent never goes below this
  maxMidi: 88, // E6: the ascent never goes above this
};

// ---- State -----------------------------------------------------------------------------------

export type RangeDirection = 'lower' | 'higher';

export type RangeFinderPhase = 'permission' | 'listen' | 'step' | 'done' | 'result' | 'failed';

// The page plays each requestId once; a new request is a new note or the same note again
export interface GuideRequest {
  requestId: number;
  midi: number;
}

// What a hold is holding: the singer at or past the target - one kind, because to them they are the
// same thing - or, on a direction's first target, the octave the rule above shifts the run into.
// A `reached` hold keeps the running mean of the pitches heard, so the note it records is what the
// singer held rather than whichever frame happened to complete it.
export type StepHold =
  | { kind: 'reached'; startMs: number; lastMs: number; midiSum: number; frames: number }
  | { kind: 'octaveBelow' | 'octaveAbove'; startMs: number; lastMs: number };

export interface PermissionState {
  phase: 'permission';
  matched: number[];
}

export interface ListenState {
  phase: 'listen';
  direction: RangeDirection;
  level: number; // 0..1 for the meter
  // the echo canceller's tail past the moment the direction before this one ended: earlier frames
  // move the meter and the clock only (unset for the run's first listen, which has nothing behind it)
  scoreFromMs?: number;
  // the first frame this phase was fed, which is when its opening prompt went up on the frame
  // clock: frames before listenPromptMs past it move the meter and the clock only (unset until then)
  promptFromMs?: number;
  // the steady pitch being held, with its running mean, the last frame that carried it and how much
  // of the hold confident frames accounted for (see listenMinConfidentFraction)
  hold?: { hz: number; startMs: number; lastMs: number; midiSum: number; frames: number; confidentMs: number };
  lastToneMs?: number; // the last frame with a confident pitch, whatever pitch (see isHearingTone)
  silentMs: number; // the quiet run so far: we cannot hear the singer (see isTroubleHearing)
  noisyMs: number; // the run of sound with no confident pitch in it: a room we cannot hear through
  lastFrameMs?: number;
  nextRequestId: number; // carried across the handover so no step ever reuses a guide request id
  matched: number[];
  directionStart: number; // where this direction's notes begin in `matched` (see directionNotes)
}

export interface StepState {
  phase: 'step';
  direction: RangeDirection;
  targetMidi: number;
  guide: GuideRequest;
  listenFromMs?: number; // set by guideStarted; frames before it are ignored
  hold?: StepHold;
  lastToneMs?: number; // the last frame scored `reached`: the singer on the note (see isHearingTone)
  tryingMs: number; // voiced sound that is not far enough: the hint (see isHinted), then the end
  silentMs: number;
  nudged: boolean; // the silence nudge has the line under the prompt, until the singer sings again
  firstNote: boolean; // the octave rule applies: this direction's first target, not yet settled
  matched: number[]; // every note in the range so far: both sung notes and every matched target
  directionStart: number; // where this direction's notes begin in `matched` (see directionNotes)
  level: number; // 0..1 for the meter
  lastFrameMs?: number;
}

// A direction is over and the scene is saying so (the singer hopping onto the landing, and for the
// descent the handover to the ascent): nothing is scored until the page dispatches continueAfterDone
export interface DoneState {
  phase: 'done';
  direction: RangeDirection;
  matched: number[];
  directionStart: number;
  level: number; // 0..1 for the meter
  lastFrameMs?: number;
  // the frame the direction ended on, which is the frame the guide was cancelled at: what the
  // listen phase that follows counts the canceller's tail from (see the handover settle above)
  endedAtMs?: number;
  nextRequestId: number; // the next guide request id, carried on to whatever follows
}

export interface ResultState {
  phase: 'result';
  range: VocalRange;
  part: VoicePart;
  matched: number[];
}

export interface FailedState {
  phase: 'failed';
  failure: MicFailure; // the microphone is the only failure left (issue #3510)
  matched: number[];
}

export type RangeFinderState = PermissionState | ListenState | StepState | DoneState | ResultState | FailedState;

// ---- Actions ---------------------------------------------------------------------------------

export type RangeFinderAction =
  | { type: 'micOpened' } // permission: the mic track is open, on to the first listen phase
  | { type: 'micFailed'; failure: MicFailure } // permission: the mic could not be opened
  | { type: 'frame'; frame: CaptureFrame } // one analysed frame from the mic
  | { type: 'guideStarted'; requestId: number; atMs: number } // the page scheduled the guide for `atMs`
  | { type: 'replay' } // step: the same target again; hold, silence and nudge reset, tryingMs kept
  | { type: 'stopDirection' } // step: "That's as low / high as I go"
  | { type: 'continueAfterDone' }; // done: the scene has finished ending the direction

export function initialRangeFinderState(): RangeFinderState {
  return { phase: 'permission', matched: [] };
}

export function reduceRangeFinder(state: RangeFinderState, action: RangeFinderAction): RangeFinderState {
  switch (action.type) {
    case 'micOpened':
      return state.phase === 'permission' ? listenState('lower', state.matched) : state;
    case 'micFailed':
      return state.phase === 'permission' ? failed(action.failure, state.matched) : state;
    case 'frame':
      return frame(state, action.frame);
    case 'guideStarted':
      return guideStarted(state, action.requestId, action.atMs);
    case 'replay':
      return state.phase === 'step' ? replay(state) : state;
    case 'stopDirection':
      return state.phase === 'step' ? endDirection(state) : state;
    case 'continueAfterDone':
      return state.phase === 'done' ? afterDirection(state) : state;
  }
}

// ---- Transitions -----------------------------------------------------------------------------

// A direction opens by listening for a note of the singer's own; the meter, the frame clock and the
// guide request counter carry over from whatever came before, the hold and the silence and noise
// counts start fresh, and a phase that follows a direction scores nothing until `scoreFromMs`, the
// settle its caller works out from the frame that direction ended on (see the handover settle above).
// Its prompt window has not started: the first frame it is fed starts it (see listenFrame).
// A listen phase is where a direction begins, so every note already recorded belongs to the one
// before it: `directionStart` is simply how many there are.
function listenState(
  direction: RangeDirection,
  matched: number[],
  level = 0,
  lastFrameMs?: number,
  nextRequestId = 1,
  scoreFromMs?: number,
): ListenState {
  return {
    phase: 'listen',
    direction,
    level,
    scoreFromMs,
    silentMs: 0,
    noisyMs: 0,
    lastFrameMs,
    nextRequestId,
    matched,
    directionStart: matched.length,
  };
}

function failed(failure: MicFailure, matched: number[]): FailedState {
  return { phase: 'failed', failure, matched };
}

function guideStarted(state: RangeFinderState, requestId: number, atMs: number): RangeFinderState {
  if (state.phase !== 'step' || state.guide.requestId !== requestId) {
    return state; // a request the machine has since replaced
  }
  const { guideMs, guideTailMs } = rangeFinderTunables;
  return { ...state, listenFromMs: atMs + guideMs + guideTailMs };
}

// The listen phase heard a note held: it counts as part of the range at once, and the direction's
// first guide target is one scale step past it. A sung note already at the bound has nowhere to
// step to, so the direction ends there and then.
function identified(state: ListenState, hold: NonNullable<ListenState['hold']>): RangeFinderState {
  const sungMidi = sungSemitone(hold.midiSum / hold.frames);
  const matched = [...state.matched, sungMidi];
  const targetMidi = cMajorStep(sungMidi, state.direction);
  if (!withinBounds(targetMidi)) {
    return doneState(
      state.direction,
      matched,
      state.directionStart,
      state.level,
      state.lastFrameMs,
      state.nextRequestId,
    );
  }
  return {
    phase: 'step',
    direction: state.direction,
    targetMidi,
    guide: { requestId: state.nextRequestId, midi: targetMidi },
    tryingMs: 0,
    silentMs: 0,
    nudged: false,
    firstNote: true,
    matched,
    directionStart: state.directionStart,
    level: state.level,
    lastFrameMs: state.lastFrameMs,
  };
}

// A new target in the current direction, with a fresh guide request and counters
function nextTarget(state: StepState, targetMidi: number): StepState {
  return {
    ...state,
    targetMidi,
    guide: { requestId: state.guide.requestId + 1, midi: targetMidi },
    listenFromMs: undefined,
    hold: undefined,
    // A note we have moved past is no longer a note being held: the grace that keeps "Hold it" up
    // through a dropout must not carry the last target's reading into this one
    lastToneMs: undefined,
    tryingMs: 0,
    silentMs: 0,
    nudged: false,
  };
}

// The same target again at the singer's asking: a new guide request, and the hold, the silence and
// the nudge start over. What they have already spent trying is kept, exactly as the silence nudge
// keeps it: hearing the note a second time is not a new target, so the hint stands where it stood.
// Kept only as far as the hint, though: a singer who asks for the note again a moment before the
// give-up would otherwise have the direction ended by the first frame they sang into it, which is
// no answer to "play it again". The hint stays up and they get the rest of the budget back.
function replay(state: StepState): StepState {
  return {
    ...nextTarget(state, state.targetMidi),
    tryingMs: Math.min(state.tryingMs, rangeFinderTunables.hintAfterMs),
  };
}

// The same target again after silence: the nudge shows, and what the singer has already spent
// trying is kept (silence never counts toward it, so the hint and the give-up stand where they were)
function nudge(state: StepState): StepState {
  return {
    ...state,
    guide: { requestId: state.guide.requestId + 1, midi: state.targetMidi },
    listenFromMs: undefined,
    hold: undefined,
    silentMs: 0,
    nudged: true,
  };
}

function withinBounds(midi: number): boolean {
  return midi >= rangeFinderTunables.minMidi && midi <= rangeFinderTunables.maxMidi;
}

// The octave rule shifts the note this direction's listen phase identified, which is the last note
// recorded: the rule only applies while `firstNote` holds, and recording any note clears it, so
// nothing has been added since. (Giving up after 8 s of trying leaves `firstNote` standing, which
// costs nothing: the direction is over.) The shifted note is bounded the way a sung note is (see
// shiftedTarget).
function shiftSungNote(matched: number[], shift: number): number[] {
  if (shift === 0 || matched.length === 0) {
    return matched;
  }
  return [...matched.slice(0, -1), sungSemitone(matched[matched.length - 1] + shift)];
}

// The singer held the target, or a pitch past it on the direction's side: they are credited with
// the nearest scale note on the target's side of the mean of what they held, and the guide steps on
// from there. Conservative on purpose: past the target they sang something we never asked for, so
// the note recorded is the one we can be sure of rather than the pitch itself - and on the target
// the mean is the target, so the target is what goes in.
function reachedTarget(state: StepState, hold: Extract<StepHold, { kind: 'reached' }>): RangeFinderState {
  return recordNote(state, beyondNote(hold.midiSum / hold.frames, state.targetMidi, state.direction), 0);
}

// The octave rule on a direction's first target: the singer held the target an octave away, so the
// run moves into their octave - the octave of the target is recorded and the sung note moves with
// it. An octave off the target can be a note the machine does not work in (an octave below E2), so
// both notes the shift moves are clamped into the bounds rather than the shift being skipped: the
// singer did sing something, and clamping keeps a note of theirs in the range where skipping would
// record the octave they did not sing in.
function shiftedTarget(state: StepState, kind: 'octaveBelow' | 'octaveAbove'): RangeFinderState {
  const shift = kind === 'octaveBelow' ? -12 : 12;
  return recordNote(state, sungSemitone(state.targetMidi + shift), shift);
}

// A note is recorded: it joins the range (with the sung note moved too when the octave rule shifted
// the run), the first-target rule is over, and the guide steps one scale note past it - or the
// direction ends, there being nothing inside the bounds to step to
function recordNote(state: StepState, note: number, shift: number): RangeFinderState {
  const recorded: StepState = {
    ...state,
    matched: [...shiftSungNote(state.matched, shift), note],
    firstNote: false,
    hold: undefined,
  };
  const next = cMajorStep(note, state.direction);
  return withinBounds(next) ? nextTarget(recorded, next) : endDirection(recorded);
}

// A direction is over, however it ended: nothing more is scored, and the run waits on the page's
// `continueAfterDone` while the scene sees the singer onto the landing
function doneState(
  direction: RangeDirection,
  matched: number[],
  directionStart: number,
  level: number,
  lastFrameMs: number | undefined,
  nextRequestId: number,
): DoneState {
  // `endedAtMs` is this frame and stays there: the frames that follow move the clock, not the end
  return {
    phase: 'done',
    direction,
    matched,
    directionStart,
    level,
    lastFrameMs,
    endedAtMs: lastFrameMs,
    nextRequestId,
  };
}

function endDirection(state: StepState): DoneState {
  return doneState(
    state.direction,
    state.matched,
    state.directionStart,
    state.level,
    state.lastFrameMs,
    state.guide.requestId + 1,
  );
}

// The descent continues into the ascent's listen phase, which finds its own note; the ascent
// continues into the result. Both directions recorded the note their listen phase heard, so there is
// always something to make a range of. The guide request counter goes on rising across the
// handover, so a guideStarted the descent is still waiting on (a soundfont that loaded late) can
// never be taken for the ascent's first guide.
function afterDirection(state: DoneState): RangeFinderState {
  const { direction, matched, level, lastFrameMs, endedAtMs, nextRequestId } = state;
  if (direction === 'lower') {
    // The canceller's tail runs from the frame the direction ended on, not from here. The done
    // sequence has been playing since, and the ascent's prompt window (listenPromptMs) starts only
    // now, so the window is what the ascent waits for; the tail matters only if the window is ever
    // tuned shorter than it (see listenScoresFromMs)
    const scoreFromMs = endedAtMs === undefined ? undefined : endedAtMs + rangeFinderTunables.guideTailMs;
    return listenState('higher', matched, level, lastFrameMs, nextRequestId, scoreFromMs);
  }
  const range = matchedRange(matched);
  return { phase: 'result', range, part: voicePartForRange(range), matched };
}

// ---- Frames ----------------------------------------------------------------------------------

function frame(state: RangeFinderState, frame: CaptureFrame): RangeFinderState {
  switch (state.phase) {
    case 'listen':
      return listenFrame(state, frame);
    case 'step':
      return stepFrame(state, frame);
    case 'done':
      // The direction is over: the meter and the frame clock run on (the scene is still on screen),
      // `endedAtMs` stays where the direction ended, and nothing at all is scored
      return { ...state, level: levelForDbfs(frame.dbfs), lastFrameMs: frame.timeMs };
    default:
      return state;
  }
}

// This exercise's own reading of the detector: a silent frame carries no pitch worth trusting,
// however confident YIN is about the room. (frameDelta, isSilent and levelForDbfs are the shared
// ones in audio/frame_reading.ts; only this question is answered differently here. isSilent is
// always asked here without its `heard` flag: this exercise reads every frame against the one
// entry bar, and the hysteresis below it is Pitch Match's.)
function confidentPitch(frame: CaptureFrame): { frequency: number; clarity: number } | undefined {
  if (isSilent(frame)) {
    return undefined;
  }
  return frame.pitch && frame.pitch.clarity >= rangeFinderTunables.voicedClarity ? frame.pitch : undefined;
}

// The listen hold this frame carries on, if it carries one on at all
function carriedListenHold(
  hold: ListenState['hold'],
  hz: number,
  timeMs: number,
  steadyCents: number,
): ListenState['hold'] {
  const carried = holdAfterGap(hold, timeMs);
  if (!carried) {
    return undefined;
  }
  return Math.abs(centsBetween(hz, carried.hz)) <= steadyCents ? carried : undefined;
}

// The first frame a listen phase scores: the end of its opening prompt's window, or the end of the
// canceller's settle after the direction before it if that comes later (see the header)
function listenScoresFromMs(state: ListenState, promptFromMs: number): number {
  const promptEndsMs = promptFromMs + rangeFinderTunables.listenPromptMs;
  return state.scoreFromMs === undefined ? promptEndsMs : Math.max(state.scoreFromMs, promptEndsMs);
}

function listenFrame(state: ListenState, frame: CaptureFrame): RangeFinderState {
  const { listenSteadyCents, listenHoldMs, listenMinConfidentFraction } = rangeFinderTunables;
  const delta = frameDelta(state.lastFrameMs, frame.timeMs);
  // The phase's first frame is when its prompt went up on the frame clock, and stays that
  const promptFromMs = state.promptFromMs ?? frame.timeMs;
  const base: ListenState = { ...state, promptFromMs, level: levelForDbfs(frame.dbfs), lastFrameMs: frame.timeMs };
  if (frame.timeMs < listenScoresFromMs(state, promptFromMs)) {
    // The opening prompt is still being read, or the echo canceller is still settling after the
    // guide the direction just ended cancelled: the meter and the clock move on and nothing counts.
    // No hold, no tone, no silence and no noise, so the scene goes on saying the prompt. There is
    // no hold to throw away - a listen phase starts without one and nothing before this can start one.
    return base;
  }
  const pitch = confidentPitch(frame);
  if (!pitch) {
    // No note either way, but the two kinds of nothing are worth telling apart: quiet is a singer we
    // cannot hear, and sound we can make no note of is a room we cannot hear them through. Each is
    // counted on its own so the screen can say which, and neither is ever a failure. Each count is
    // the run of that one kind going on now, so the other is cleared as it starts: the bubble
    // follows the room rather than standing on a count the room stopped earning seconds ago.
    // The hold is not thrown away with them: a frame or two of nothing inside the gap tolerance is
    // a dip in a held note, not the end of one (issue #3559).
    const hold = holdAfterGap(state.hold, frame.timeMs);
    return isSilent(frame)
      ? { ...base, silentMs: state.silentMs + delta, noisyMs: 0, hold }
      : { ...base, noisyMs: state.noisyMs + delta, silentMs: 0, hold };
  }
  // A confident pitch is a tone heard, whichever pitch it is: the hold starts over when the singer
  // drifts, but the scene goes on hearing them sing (see isHearingTone)
  const heard: ListenState = { ...base, silentMs: 0, noisyMs: 0, lastToneMs: frame.timeMs };
  // The hold goes on only while the pitch is the same one and the frames keep coming: a gap longer
  // than matchGapMs is not one held note either side of it, however steady the two ends are
  const carried = carriedListenHold(state.hold, pitch.frequency, frame.timeMs, listenSteadyCents);
  const midi = frequencyToMidi(pitch.frequency);
  const hold = carried
    ? {
        ...carried,
        lastMs: frame.timeMs,
        midiSum: carried.midiSum + midi,
        frames: carried.frames + 1,
        // the audio time this confident frame accounts for: what the hold has really been heard for,
        // as against the span the grace keeps it alive over
        confidentMs: carried.confidentMs + delta,
      }
    : { hz: pitch.frequency, startMs: frame.timeMs, lastMs: frame.timeMs, midiSum: midi, frames: 1, confidentMs: 0 };
  // Long enough, and confident for enough of it: a held note reads confidently in nearly every
  // frame, so a hold the grace has carried through mostly unconfident ones is the room, not a singer
  if (frame.timeMs - hold.startMs >= listenHoldMs && hold.confidentMs >= listenHoldMs * listenMinConfidentFraction) {
    return identified({ ...heard, hold }, hold);
  }
  return { ...heard, hold };
}

type FrameKind = 'silent' | 'match' | 'octaveBelow' | 'octaveAbove' | 'unmatched';

// A frame worth holding on to: the singer at or past the target, with the pitch they are really on
// so the hold can keep its mean, or an octave off it on a first target (the octave rule's business)
type HeldFrame = { kind: 'reached'; midi: number } | { kind: 'octaveBelow' | 'octaveAbove' };

// What a scored frame says about the singer relative to the target, in this direction
type ScoredFrame = { kind: 'silent' } | { kind: 'trying' } | HeldFrame;

// What a listening frame says about the singer relative to the target. The known-target estimator
// cannot tell a singer at the target from one an octave above by correlation alone (half the
// period also divides the full period), so YIN, whose known errors run an octave down, is trusted
// when it reads an octave up. A singer an octave below shows as a weak correlation at the target's
// period and a strong one at twice the period.
function classify(frame: CaptureFrame, targetMidi: number): FrameKind {
  const { matchCents, voicedClarity, octaveMargin } = rangeFinderTunables;
  if (isSilent(frame)) {
    return 'silent';
  }
  const target = frame.target;
  if (!target) {
    return 'unmatched';
  }
  if (target.clarity >= voicedClarity && Math.abs(target.cents) <= matchCents) {
    if (target.octaveBelow >= target.clarity + octaveMargin) {
      return 'octaveBelow';
    }
    const pitch = confidentPitch(frame);
    const yinOctaveAbove =
      pitch !== undefined && Math.abs(centsBetween(pitch.frequency, midiToFrequency(targetMidi)) - 1200) <= matchCents;
    if (target.octaveAbove >= target.clarity + octaveMargin || yinOctaveAbove) {
      return 'octaveAbove';
    }
    return 'match';
  }
  if (target.octaveBelow >= voicedClarity && target.octaveBelow >= target.clarity + octaveMargin) {
    return 'octaveBelow';
  }
  return 'unmatched';
}

// The direction's own reading of a frame. On the target and past it on the direction's side are one
// reading, `reached`, carrying the midi the singer is really on so a hold can average it: the target
// plus the estimator's cents when they are on it, the octave when that is what was recognised, and
// YIN's pitch when they have gone their own way. An octave on either side of a direction's first
// target is the octave rule's (the listen phase read their note an octave off); after that target
// the wrong-side octave is only trying.
function scoreFrame(frame: CaptureFrame, state: StepState): ScoredFrame {
  const { direction, targetMidi, firstNote } = state;
  const down = direction === 'lower';
  const kind = classify(frame, targetMidi);
  if (kind === 'silent') {
    return { kind };
  }
  if (kind === 'match') {
    return { kind: 'reached', midi: targetMidi + (frame.target?.cents ?? 0) / 100 };
  }
  if (kind === 'octaveBelow' || kind === 'octaveAbove') {
    if (firstNote) {
      return { kind };
    }
    return kind === (down ? 'octaveBelow' : 'octaveAbove')
      ? { kind: 'reached', midi: targetMidi + (down ? -12 : 12) }
      : { kind: 'trying' };
  }
  const pitch = confidentPitch(frame);
  if (pitch === undefined) {
    return { kind: 'trying' }; // sound we can make no note of at all: they are having a go
  }
  const { matchCents, voicedClarity } = rangeFinderTunables;
  const cents = centsBetween(pitch.frequency, midiToFrequency(targetMidi));
  const past = down ? cents < -matchCents : cents > matchCents;
  if (!past) {
    return { kind: 'trying' };
  }
  if (nearAnOctavePast(cents)) {
    // A reading sitting on an exact octave past the note we played is more likely YIN's own octave
    // error than a singer: its known errors run an octave down, which is exactly the way the descent
    // travels. So it counts only when the estimator hears the same thing (and the ascent is held to
    // the same rule, for one rule rather than two).
    const corroboration = down ? frame.target?.octaveBelow : frame.target?.octaveAbove;
    if (corroboration === undefined || corroboration < voicedClarity) {
      return { kind: 'trying' };
    }
  }
  return { kind: 'reached', midi: frequencyToMidi(pitch.frequency) };
}

// Is this reading near enough an exact octave past the target to be YIN's octave error? The guard
// is narrow on purpose, and it has to be: the estimator's octave bands are the only thing that can
// corroborate a reading, and it computes them at twice and half the target's period (±150 cents),
// so a reading anywhere else past the target can never be corroborated and would be stuck "trying"
// for ever. A singer who really drops a fifth, or a seventh, is simply believed.
function nearAnOctavePast(cents: number): boolean {
  const { octaveGuardCents } = rangeFinderTunables;
  return [1200, 2400].some((octave) => Math.abs(Math.abs(cents) - octave) <= octaveGuardCents);
}

// The hold this frame starts or carries on. A hold the frames stopped feeding for longer than
// matchGapMs starts over here as well as on a silent or trying frame: without that, two frames
// either side of a stalled worker would complete a hold between them.
function extendHold(hold: StepHold | undefined, scored: HeldFrame, timeMs: number): StepHold {
  const carried = holdAfterGap(hold, timeMs);
  if (scored.kind === 'reached') {
    const held = carried?.kind === 'reached' ? carried : { startMs: timeMs, midiSum: 0, frames: 0 };
    return {
      kind: 'reached',
      startMs: held.startMs,
      lastMs: timeMs,
      midiSum: held.midiSum + scored.midi,
      frames: held.frames + 1,
    };
  }
  if (carried?.kind === scored.kind) {
    return { ...carried, lastMs: timeMs };
  }
  return { kind: scored.kind, startMs: timeMs, lastMs: timeMs };
}

// A hold of either kind - a listen phase's steady pitch, a step's reached or octave hold - survives
// a dip shorter than matchGapMs, whether the frames stopped arriving or arrived with nothing in them
// the detector was confident about (issue #3559): either way the singer is still on the note they
// were on, and only a longer dip is one note that stopped and another that started. The hold keeps
// everything it had counted, so what it earns is still the mean of what was actually heard.
function holdAfterGap<Hold extends { lastMs: number }>(hold: Hold | undefined, timeMs: number): Hold | undefined {
  if (hold && timeMs - hold.lastMs > rangeFinderTunables.matchGapMs) {
    return undefined;
  }
  return hold;
}

function stepFrame(state: StepState, frame: CaptureFrame): RangeFinderState {
  const { silenceReplayMs, giveUpAfterMs, matchHoldMs } = rangeFinderTunables;
  const delta = frameDelta(state.lastFrameMs, frame.timeMs);
  const base: StepState = { ...state, level: levelForDbfs(frame.dbfs), lastFrameMs: frame.timeMs };
  if (state.listenFromMs === undefined || frame.timeMs < state.listenFromMs) {
    return base; // the guide is sounding (or about to): nothing is scored
  }
  const scored = scoreFrame(frame, state);
  if (scored.kind === 'silent') {
    const silentMs = state.silentMs + delta;
    if (silentMs >= silenceReplayMs) {
      return nudge(base);
    }
    return { ...base, silentMs, hold: holdAfterGap(state.hold, frame.timeMs) };
  }
  // Anything else is sound, so the silence is over. The nudge asked for a note, so only a note
  // dismisses it: a breath or a room noise is not the singer answering, and clearing the line on
  // one would take the nudge away before it had been read. Once a pitch does arrive the line goes
  // back to the hint (see isHinted), which is the one that says what to do differently.
  const voiced: StepState = { ...base, silentMs: 0, nudged: state.nudged && confidentPitch(frame) === undefined };
  if (scored.kind === 'trying') {
    // They are singing, but not as far as the note we played: tell them which way, and if they
    // keep at it without getting there, that is as far as they go
    const tryingMs = state.tryingMs + delta;
    if (tryingMs >= giveUpAfterMs) {
      return endDirection(voiced);
    }
    return { ...voiced, tryingMs, hold: holdAfterGap(state.hold, frame.timeMs) };
  }
  const hold = extendHold(state.hold, scored, frame.timeMs);
  // "Hold it" on a stepping screen means the singer is at or past the note we played, so only a
  // reached frame is a tone heard: the octave holds a first target may start are the machine's
  // business, not something the singer is being asked to keep up
  const held: StepState = {
    ...voiced,
    hold,
    lastToneMs: scored.kind === 'reached' ? frame.timeMs : state.lastToneMs,
  };
  if (frame.timeMs - hold.startMs < matchHoldMs) {
    return held;
  }
  return hold.kind === 'reached' ? reachedTarget(held, hold) : shiftedTarget(held, hold.kind);
}

// ---- Notes -----------------------------------------------------------------------------------

// The C major scale notes the ladder steps by are the naturals Pitch Match draws its targets from,
// so the check itself is isNatural in common/src/pitch_match.ts and only the stepping lives here.

// The next C major scale note above or below `midi` (a note off the scale steps to the nearest
// scale note in that direction)
export function cMajorStep(midi: number, direction: RangeDirection): number {
  const step = direction === 'higher' ? 1 : -1;
  let candidate = Math.round(midi) + step;
  while (!isNatural(candidate)) {
    candidate += step;
  }
  return candidate;
}

// The note a hold at or past the target earns: its mean pitch rounded to a semitone, then the
// nearest C major scale note on the target's side of it (at or above what they sang going lower),
// never past the target itself and never outside the bounds. They may well sing lower than this -
// they just have not been asked to, so it is not recorded. The cap at the target is not only for
// overshoots: a hold on the target has a mean of its own, and one that sits a few cents on the near
// side would otherwise round to the next semitone past the note we actually played.
export function beyondNote(meanMidi: number, targetMidi: number, direction: RangeDirection): number {
  const rounded = Math.round(meanMidi);
  const backToward = direction === 'lower' ? 'higher' : 'lower';
  const onScale = isNatural(rounded) ? rounded : cMajorStep(rounded, backToward);
  return sungSemitone(direction === 'lower' ? Math.min(onScale, targetMidi) : Math.max(onScale, targetMidi));
}

// The semitone a listen phase settled on: its mean pitch rounded, kept inside the bounds
export function sungSemitone(meanMidi: number): number {
  const { minMidi, maxMidi } = rangeFinderTunables;
  return Math.min(maxMidi, Math.max(minMidi, Math.round(meanMidi)));
}

// The extremes of the notes in the range. Both directions record the note their listen phase
// heard before anything else can end them, so the result is never reached with none.
export function matchedRange(matched: number[]): VocalRange {
  if (matched.length === 0) {
    throw new Error('the Range Finder reaches its result only with a note recorded');
  }
  return { lowMidi: Math.min(...matched), highMidi: Math.max(...matched) };
}

// ---- Selectors -------------------------------------------------------------------------------

// Which of the header's three progress segments the run is on (issue #3510): 1 the microphone,
// 2 going lower (listening and stepping alike), 3 going higher and the result. A microphone
// failure keeps the segment of the step it interrupted, which is always the first.
export function progressSegment(state: RangeFinderState): 1 | 2 | 3 {
  switch (state.phase) {
    case 'permission':
      return 1;
    case 'listen':
    case 'step':
    case 'done':
      // The descent's done keeps the second segment: the third fills at the handover, when the
      // ascent's listen phase begins
      return state.direction === 'lower' ? 2 : 3;
    case 'result':
      return 3;
    case 'failed':
      return 1;
  }
}

// A listen phase has gone long enough in silence to tell the singer we cannot hear them. Derived
// from the count rather than stored beside it, so no state can show the message without the silence
// that earns it.
export function isTroubleHearing(state: RangeFinderState): boolean {
  return state.phase === 'listen' && state.silentMs >= rangeFinderTunables.noToneAfterMs;
}

// The same, for the other kind of nothing: as long a stretch of sound the machine can make no note
// of, which is a room to move out of rather than a voice to raise
export function isNoisy(state: RangeFinderState): boolean {
  return state.phase === 'listen' && state.noisyMs >= rangeFinderTunables.noToneAfterMs;
}

// Is the singer being heard on a note now? What the scene's "Hold it" keys off, in a listen phase
// (any confident pitch) and while a step's hold has reached the target (issue #3510, addendum 4).
// The detector's confidence dips between frames while a singer holds a note, so the bubble would
// flicker between "Hold it" and the prompt if it followed the hold itself: this is smoothed by
// toneGraceMs, the last tone standing for that long past the frame that carried it. A pitch change
// restarts the hold but not the grace - they are still singing. Only the bubble is smoothed: what
// the machine credits is decided by the holds, which are untouched.
export function isHearingTone(state: RangeFinderState): boolean {
  if (state.phase !== 'listen' && state.phase !== 'step') {
    return false;
  }
  const { lastFrameMs, lastToneMs } = state;
  if (lastFrameMs === undefined || lastToneMs === undefined) {
    return false;
  }
  return lastFrameMs - lastToneMs <= rangeFinderTunables.toneGraceMs;
}

// The notes credited in the direction the run is on, in order: what the scene draws a tread each
// for. Empty before the direction's listen phase has heard anything, and empty in the phases that
// belong to no direction.
export function directionNotes(state: RangeFinderState): number[] {
  if (state.phase !== 'listen' && state.phase !== 'step' && state.phase !== 'done') {
    return [];
  }
  return state.matched.slice(state.directionStart);
}

// The singer has spent long enough on one target without reaching it for the screen to say which
// way to go. Derived from the time spent trying rather than stored beside it, so nothing can leave
// the hint showing after the state that earned it has gone: a new target clears tryingMs and the
// hint with it, while a replay or a nudge keeps both.
export function isHinted(state: RangeFinderState): boolean {
  return state.phase === 'step' && state.tryingMs >= rangeFinderTunables.hintAfterMs;
}

// The guide has sounded and frames are being scored against the target
export function isScoring(state: RangeFinderState): boolean {
  return (
    state.phase === 'step' &&
    state.listenFromMs !== undefined &&
    state.lastFrameMs !== undefined &&
    state.lastFrameMs >= state.listenFromMs
  );
}
